/**
 * Cart Recommendations API Fetcher
 *
 * Fetches product recommendations from Shopify's Recommendations API
 * when metafield data is not available
 */

class CartRecommendationsAPIFetcher {
  static async fetchRecommendations(
    productId,
    intent = "complementary",
    limit = 10
  ) {
    try {
      const url = `${Theme.routes.product_recommendations_url}?product_id=${productId}&intent=${intent}&limit=${limit}`;

      const response = await fetch(url, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Extract product data from the response
      const productCards = doc.querySelectorAll("[data-product-id]");
      const products = [];

      productCards.forEach((card) => {
        const productId = card.getAttribute("data-product-id");
        const title =
          card.querySelector(".cart-recommendation__title")?.textContent || "";
        const imageUrl =
          card.querySelector(".cart-recommendation__image")?.src || "";
        const price =
          card.querySelector(".cart-recommendation__price")?.textContent || "";
        const url =
          card.querySelector(".cart-recommendation__image-link")?.href || "";
        const variantId = card.querySelector('input[name="id"]')?.value || "";

        products.push({
          id: productId,
          title,
          imageUrl,
          price,
          url,
          variantId,
        });
      });

      return products;
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      return [];
    }
  }

  static async injectRecommendations(
    container,
    productId,
    intent = "complementary"
  ) {
    const products = await this.fetchRecommendations(productId, intent);

    if (products.length === 0) {
      container.classList.add("cart-recommendations--empty");
      return;
    }

    const track = container.querySelector(
      ".cart-recommendations__slider-track"
    );
    if (!track) return;

    // Clear loading state
    const loading = container.querySelector(".cart-recommendations__loading");
    if (loading) loading.remove();

    // Add products to track
    products.forEach((product) => {
      const card = this.createProductCard(product);
      track.appendChild(card);
    });

    // Re-initialize slider
    if (window.CartRecommendationsSlider) {
      new window.CartRecommendationsSlider(
        container.closest('[data-module="cart-recommendations-slider"]') ||
          container
      );
    }
  }

  static createProductCard(product) {
    const div = document.createElement("div");
    div.className = "cart-recommendation__item";
    div.setAttribute("data-product-id", product.id);
    div.innerHTML = `
      <div class="cart-recommendation__image-wrapper">
        <a href="${product.url}" class="cart-recommendation__image-link">
          <img
            src="${product.imageUrl}"
            alt="${product.title}"
            class="cart-recommendation__image"
            loading="lazy"
            decoding="async"
          />
        </a>
      </div>

      <div class="cart-recommendation__content">
        <a href="${product.url}" class="cart-recommendation__title">
          ${product.title}
        </a>

        <div class="cart-recommendation__price">
          ${product.price}
        </div>

        <form method="post" action="${Theme.routes.cart_add_url}" class="cart-recommendation__form" data-product-id="${product.id}">
          <input type="hidden" name="id" value="${product.variantId}" />
          <button
            type="submit"
            class="cart-recommendation__add-btn button button-primary"
            aria-label="Add ${product.title} to cart"
          >
            <span class="cart-recommendation__add-btn-text">Add to cart</span>
            <span class="cart-recommendation__add-btn-loading" aria-hidden="true">Adding...</span>
          </button>
        </form>
      </div>
    `;

    return div;
  }
}

// Auto-initialize when recommendations are needed
document.addEventListener("DOMContentLoaded", () => {
  const recommendations = document.querySelector(
    "cart-recommendations-component"
  );

  if (
    recommendations &&
    recommendations.dataset.hasRecommendations === "false"
  ) {
    // Get the first product from cart to base recommendations on
    fetch(`${Theme.routes.cart_url}.json`)
      .then((response) => response.json())
      .then((data) => {
        if (data.items && data.items.length > 0) {
          const firstProduct = data.items[0].product_id;
          CartRecommendationsAPIFetcher.injectRecommendations(
            recommendations,
            firstProduct,
            "complementary"
          );
        }
      })
      .catch((error) => console.error("Error fetching cart:", error));
  }
});

Object.defineProperty(window, "CartRecommendationsAPIFetcher", {
  value: CartRecommendationsAPIFetcher,
  writable: false,
  configurable: true,
});
